import { WEB_ORGANIZATION_CONFIGURABLE_PROVIDERS } from "@frontend-shared/constants";
import {
  EGlobalSearchGate,
  EIntegrationProvider,
  type IParsedProject,
} from "@frontend-shared/types";
import {
  getMemoryPolicyAccess,
  getSiemConfigAccess,
} from "@frontend-shared/utils";

import type {
  IBuildGlobalSearchGateStateProps,
  IGlobalSearchOrganizationInputs,
  TGlobalSearchGateState,
} from "./types";

type TProjectPermission = keyof IParsedProject["permissions"];

interface IHasOrganizationPermissionProps {
  organization: IGlobalSearchOrganizationInputs;
  permission: TProjectPermission;
}

// NOTE: The organization settings shell's rule (settings/organization/details/
// index.tsx:237-258): the owner, or any project of the organization granting it.
const hasOrganizationPermission = ({
  organization,
  permission,
}: IHasOrganizationPermissionProps): boolean =>
  organization.isOrganizationOwner ||
  organization.organizationProjects.some(
    (project) => project.permissions[permission],
  );

interface IIsIntegrationEnabledProps {
  organization: IGlobalSearchOrganizationInputs;
  provider: EIntegrationProvider;
}

const isIntegrationEnabled = ({
  organization,
  provider,
}: IIsIntegrationEnabledProps): boolean =>
  organization.enabledIntegrations?.includes(provider) ?? false;

// NOTE: NSAI-6589 — web's answer to every EGlobalSearchGate, computed from the
// same state its routes, tab lists and settings sections read (each gate's note
// in frontend-shared/types/global-search-index.ts names the web source). The
// Record type makes a gate added to the enum a compile error here until web
// answers it.
export const buildGlobalSearchGateState = ({
  permissions,
  flags,
  organization,
  hasPasswordSignIn,
}: IBuildGlobalSearchGateStateProps): TGlobalSearchGateState => {
  const isInOrganization = !organization.isPersonalProject;
  const isOwner = isInOrganization && organization.isOrganizationOwner;
  const accessProps = {
    isOwner: organization.isOrganizationOwner,
    projects: [...organization.organizationProjects],
  };
  const hasAnalyticsRead =
    permissions.hasAllMembersAnalyticsRead || permissions.hasAnalyticsRead;
  const hasCustomIntegrationAccess =
    flags.isCustomIntegrationEnabled &&
    (permissions.hasCustomIntegrationRead ||
      permissions.hasCustomIntegrationWrite ||
      permissions.hasCustomIntegrationDelete);
  const isOrgIntegrationConfigurable =
    organization.enabledIntegrations === null ||
    WEB_ORGANIZATION_CONFIGURABLE_PROVIDERS.some((provider) =>
      isIntegrationEnabled({ organization, provider }),
    );
  const isEnabled = (provider: EIntegrationProvider): boolean =>
    isIntegrationEnabled({ organization, provider });
  const hasOrgPermission = (permission: TProjectPermission): boolean =>
    isInOrganization && hasOrganizationPermission({ organization, permission });

  return {
    [EGlobalSearchGate.PLATFORM_WEB]: true,
    [EGlobalSearchGate.PLATFORM_MOBILE]: false,

    [EGlobalSearchGate.IN_ORGANIZATION]: isInOrganization,
    [EGlobalSearchGate.ORG_OWNER]: isOwner,

    [EGlobalSearchGate.ASSISTANT_READ]:
      permissions.hasPrivateAssistantRead || permissions.hasPublicAssistantRead,
    [EGlobalSearchGate.KNOWLEDGE_READ]: permissions.hasKnowledgeRead,
    [EGlobalSearchGate.MODEL_READ]: permissions.hasModelRead,
    [EGlobalSearchGate.PROMPT_LIBRARY_READ]: permissions.hasPromptLibraryRead,
    [EGlobalSearchGate.ANALYTICS_PAGE]:
      hasAnalyticsRead || permissions.hasProbeRead,
    [EGlobalSearchGate.ANALYTICS_READ]: hasAnalyticsRead,
    [EGlobalSearchGate.PROBE_READ]: permissions.hasProbeRead,
    [EGlobalSearchGate.AGENTS_ENABLED]: flags.isAgentsEnabled,
    [EGlobalSearchGate.AGENTS_READ]: permissions.hasAutomationRead,
    [EGlobalSearchGate.MARKETPLACE_AUTOMATION_READ]:
      permissions.hasMarketplaceAutomationRead,
    [EGlobalSearchGate.ANONYMIZATION_LAB_ENABLED]:
      flags.isAnonymizationLabEnabled,
    [EGlobalSearchGate.DOC_RECOGNITION_ENABLED]: flags.isDocRecognitionEnabled,
    [EGlobalSearchGate.DOC_RECOGNITION_READ]: permissions.hasDocRecognitionRead,
    [EGlobalSearchGate.SKILLS_ENABLED]: flags.isSkillsEnabled,
    [EGlobalSearchGate.SKILL_READ]: permissions.hasAnySkillRead,
    [EGlobalSearchGate.SKILL_CATALOG_READ]: permissions.hasSkillCatalogRead,
    [EGlobalSearchGate.INTEGRATIONS_PAGE]:
      permissions.hasIntegrationRead ||
      (permissions.hasCustomIntegrationRead &&
        flags.isCustomIntegrationEnabled),
    [EGlobalSearchGate.INTEGRATION_READ]: permissions.hasIntegrationRead,
    [EGlobalSearchGate.CUSTOM_INTEGRATION_ACCESS]: hasCustomIntegrationAccess,
    [EGlobalSearchGate.CALL_ASSISTANT_ENABLED]: flags.isCallAssistantEnabled,
    [EGlobalSearchGate.CALL_ASSISTANT_READ]: permissions.hasCallAssistantRead,

    [EGlobalSearchGate.BILLING_AVAILABLE]:
      !flags.isOnPremise && !flags.isBillingDisabled,
    [EGlobalSearchGate.NOT_ON_PREMISE]: !flags.isOnPremise,
    [EGlobalSearchGate.PASSWORD_SIGN_IN]: hasPasswordSignIn,
    [EGlobalSearchGate.EXTERNAL_API_ENABLED]: flags.isExternalApiEnabled,
    [EGlobalSearchGate.USER_MEMORY_ENABLED]: flags.isUserMemoryEnabled,
    [EGlobalSearchGate.BOT_SETTINGS_ENABLED]:
      flags.isSlackBotEnabled ||
      flags.isTeamsBotEnabled ||
      flags.isMatrixBotEnabled,
    [EGlobalSearchGate.SLACK_BOT_ENABLED]: flags.isSlackBotEnabled,
    [EGlobalSearchGate.TEAMS_BOT_ENABLED]: flags.isTeamsBotEnabled,
    [EGlobalSearchGate.MATRIX_BOT_ENABLED]: flags.isMatrixBotEnabled,
    [EGlobalSearchGate.TELEGRAM_BOT_ENABLED]: flags.isTelegramBotEnabled,
    [EGlobalSearchGate.WHATSAPP_BOT_ENABLED]: flags.isWhatsappBotEnabled,
    [EGlobalSearchGate.TOKEN_USAGE_ENABLED]: flags.isTokenUsageEnabled,
    [EGlobalSearchGate.TOKEN_COST_USAGE_ENABLED]: flags.isTokenCostUsageEnabled,
    [EGlobalSearchGate.TOKEN_LIMITS_ENABLED]: flags.isTokenLimitsEnabled,
    [EGlobalSearchGate.TTS_VOICE_CHOICE]:
      flags.isTtsEnabled && flags.ttsVoiceCount > 1,
    // NOTE: PLATFORM-SPECIFIC — web renders the theme picker everywhere.
    [EGlobalSearchGate.THEME_PICKER]: true,

    [EGlobalSearchGate.ORG_PROJECT_READ]: hasOrgPermission("canReadProject"),
    [EGlobalSearchGate.ORG_ROLE_READ]: hasOrgPermission("canReadProjectRole"),
    [EGlobalSearchGate.ORG_ROLE_WRITE]: hasOrgPermission("canWriteProjectRole"),
    [EGlobalSearchGate.ORG_API_KEY_READ]: hasOrgPermission("canReadApiKeys"),
    [EGlobalSearchGate.ORG_MEMBER_INVITE]: hasOrgPermission(
      "canInviteMembersToProject",
    ),
    [EGlobalSearchGate.ORG_USAGE_LIMITS_READ]:
      hasOrgPermission("canReadUsageLimits"),
    [EGlobalSearchGate.ORG_SIEM_CONFIG_READ]:
      isInOrganization && getSiemConfigAccess(accessProps).canRead,
    [EGlobalSearchGate.ORG_MEMORY_POLICY_READ]:
      isInOrganization && getMemoryPolicyAccess(accessProps).canRead,
    [EGlobalSearchGate.ORG_CONFIGURABLE_INTEGRATION]:
      isOrgIntegrationConfigurable,
    // NOTE: PLATFORM-SPECIFIC — web always has the Appearance tab (owner-only
    // through ORG_OWNER).
    [EGlobalSearchGate.ORG_APPEARANCE_AVAILABLE]: true,
    [EGlobalSearchGate.SYNCATOR_API_ENABLED]: flags.isSyncatorApiEnabled,
    [EGlobalSearchGate.SIEM_EXPORT_ENABLED]: flags.isSiemExportEnabled,
    [EGlobalSearchGate.BRANDED_PRESENTATIONS_ENABLED]:
      flags.isBrandedPresentationsEnabled,
    [EGlobalSearchGate.FINANCIAL_MASKING_ENABLED]:
      flags.isFinancialMaskingEnabled,
    [EGlobalSearchGate.PII_CUSTOM_ENTITIES_ENABLED]:
      flags.isPiiCustomEntitiesEnabled,
    [EGlobalSearchGate.PII_ADVANCED_RULES_ENABLED]:
      flags.isPiiAdvancedRulesEnabled,
    [EGlobalSearchGate.PII_INTENT_ROUTING_ENABLED]:
      flags.isPiiIntentRoutingEnabled,
    [EGlobalSearchGate.SAML_SEAT_TIERS_ENABLED]: flags.isSamlSeatTiersEnabled,
    [EGlobalSearchGate.DOMAIN_VERIFICATION_AVAILABLE]:
      flags.isDomainVerificationAvailable,
    [EGlobalSearchGate.MODEL_TOKEN_LIMITS_ENABLED]:
      flags.isModelTokenLimitsEnabled,
    [EGlobalSearchGate.INTEGRATION_BITRIX24_ENABLED]: isEnabled(
      EIntegrationProvider.BITRIX24,
    ),
    [EGlobalSearchGate.INTEGRATION_AZURE_MES_ENABLED]: isEnabled(
      EIntegrationProvider.AZURE_MES,
    ),
    [EGlobalSearchGate.INTEGRATION_KONTUR_FOCUS_ENABLED]: isEnabled(
      EIntegrationProvider.KONTUR_FOCUS,
    ),
    [EGlobalSearchGate.INTEGRATION_KONTUR_DIADOC_ENABLED]: isEnabled(
      EIntegrationProvider.KONTUR_DIADOC,
    ),
    [EGlobalSearchGate.INTEGRATION_JIRA_DC_ENABLED]: isEnabled(
      EIntegrationProvider.ATLASSIAN_JIRA_DC,
    ),
    [EGlobalSearchGate.INTEGRATION_CONFLUENCE_DC_ENABLED]: isEnabled(
      EIntegrationProvider.ATLASSIAN_CONFLUENCE_DC,
    ),
    [EGlobalSearchGate.INTEGRATION_NEXTCLOUD_ENABLED]: isEnabled(
      EIntegrationProvider.NEXTCLOUD,
    ),
    [EGlobalSearchGate.INTEGRATION_MCPS_ENABLED]: isEnabled(
      EIntegrationProvider.MCPS,
    ),
  };
};
